import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useEffectOnce from "../hooks/useEffectOnce";

// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
import TextInput from "../components/TextInput";
import SelectInput from "../components/SelectInput";
import Loader from "../components/Loader";
import PageHeader from "../components/PageHeader";
import DateRangeFilter from "../components/DateRangeFilter";
import "../assets/css/modal.css";
import "../assets/css/reportTable.css";
import "../assets/css/tasks.css";

// --- Redux Selectors ---
import {
  selectTasksStatus,
  selectCreateTaskStatus,
  selectprojectMenu,
  selectClientMenu,
  selectGetRaisedbyDetailsList,
  selectSupportTeamTaskList,
  selectLastSyncDateTimeApiList,
  selectLastEndTimeList,
} from "../redux/tasks/taskSelectors";

// --- Redux Actions ---
import {
  supportTeamTaskCreateApis,
  projectMenuApis,
  clientMenuApis,
  raisedbyDetailsApis,
  mainMenuApis,
  supportTeamTaskListApis,
  createStatusTrackerApis,
  lastSyncDateTimeApis,
  lastEndTimeApis,
} from "../redux/tasks/taskActions";

import moment from "moment-timezone";
import { calculateDuration, formatDateTimeDisplay, getTimeDiffInSeconds } from "../utils/common";
import { useTabRefresh } from "../context/TabsContext";

// --- HELPER: Get Current Date Time in ISO Format (YYYY-MM-DDTHH:MM) ---
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const SupportTeamTask = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==============================
  // 1. LOCAL STATE
  // ==============================
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("manual");
  const [isLoading, setIsLoading] = useState(false);
  const [showRaisedByMenu, setShowRaisedByMenu] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Incomplete");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);

  // Date Filter State
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);

    return {
      startDate: start,
      endDate: end,
    };
  });

  // State to track "Now" for the min attribute
  const [minDateTime, setMinDateTime] = useState(getCurrentDateTimeLocal());

  // Form Data
  const initialFormState = {
    queryNo: "",
    project: "",
    client: "",
    raisedBy: "",
    contactNo: "",
    taskName: "",
    description: "",
    queryType: "",
    taskStatus: "Complete",
    startTime: "",
    endTime: "",
  };
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const lastRequestRef = useRef(null);
  /* Bumped when this tab is re-activated — see useTabRefresh below. */
  const [reloadKey, setReloadKey] = useState(0);

  // ==============================
  // 2. REDUX STATE
  // ==============================
  const tasksData = useSelector(selectSupportTeamTaskList);
  const projectMenuList = useSelector(selectprojectMenu);
  const lastSyncDateTime = useSelector(selectLastEndTimeList);
  const clientMenuList = useSelector(selectClientMenu);
  const raisedByList = useSelector(selectGetRaisedbyDetailsList);
  console.log(lastSyncDateTime?.data?.[0]?.EndTime, "lastSyncDateTime21211");

  const listStatus = useSelector(selectTasksStatus);
  const createStatus = useSelector(selectCreateTaskStatus);


  // --- HELPER: Get Sync Time or Fallback to Current Time ---
  const getDefaultStartTime = () => {
    const syncTime = lastSyncDateTime?.data?.[0]?.EndTime;
    return syncTime
      ? moment(syncTime).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm")
      : getCurrentDateTimeLocal();
  };



  // ==============================
  // 3. DATA PROCESSING
  // ==============================
  const filteredRaisedByList = useMemo(() => {
    if (!Array.isArray(raisedByList)) return [];
    if (!formData.raisedBy) return raisedByList;
    return raisedByList.filter((user) =>
      user.Client_UserName?.toLowerCase().includes(
        formData.raisedBy.toLowerCase(),
      ),
    );
  }, [raisedByList, formData.raisedBy]);

  const processedData = useMemo(() => {
    const rawList = Array.isArray(tasksData)
      ? tasksData
      : tasksData?.data?.data || tasksData?.data || [];
    if (!Array.isArray(rawList)) return [];

    return rawList.map((item) => {
      const fullClientName =
        item.ClientName || item.CompanyName || item.FormName || "-";
      return {
        ...item,
        DisplayClient: fullClientName,
        FormattedDate: item.QueryDate
          ? new Date(item.QueryDate).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          : "-",
      };
    });
  }, [tasksData]);

  // Dropdown Options
  const projectOptions = useMemo(
    () =>
      (Array.isArray(projectMenuList)
        ? projectMenuList
        : projectMenuList?.data || []
      ).map((i) => ({
        value: i.ProjectCode || i.ProjectID || i.id,
        label: i.ProjectName || i.Name,
      })),
    [projectMenuList],
  );
  const clientOptions = useMemo(
    () =>
      (Array.isArray(clientMenuList)
        ? clientMenuList
        : clientMenuList?.data || []
      ).map((i) => ({
        value: i.ClientCode || i.ClientID || i.id,
        label: i.ClientName || i.Name,
      })),
    [clientMenuList],
  );

  const queryTypeOptions = useMemo(
    () => [
      { value: "Bug", label: "Bug" },
      { value: "New Task", label: "New Task" },
      { value: "Rework", label: "Rework" },
    ],
    [],
  );

  const formatDateForPayload = (dateInput) => {
    if (!dateInput) return null;
    return moment(dateInput).format("YYYY-MM-DDTHH:mm:ss");
  };

  // Handler for DateRangeFilter
  const handleDateChange = (start, end) => {
    setDateRange({ startDate: start, endDate: end });
    setCurrentPage(1);
  };

  // ==============================
  // 4. API CALLS
  // ==============================
  useEffect(() => {
    const queryParam = activeTab === "manual" ? 1 : 0;
    const apiStatusFilter = statusFilter.toLowerCase();

    // Create the flat object parameters
    const params = {
      isManuallyRaised: queryParam,
      currentPage,
      pageSize,
      statusFilter: apiStatusFilter,
      fromDate: dateRange.startDate
        ? moment(dateRange.startDate).format("YYYY-MM-DD")
        : "",
      toDate: dateRange.endDate
        ? moment(dateRange.endDate).format("YYYY-MM-DD")
        : "",
    };

    const requestKey = JSON.stringify(params);

    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    dispatch(supportTeamTaskListApis(params));
  }, [
    dispatch,
    activeTab,
    currentPage,
    pageSize,
    navigate,
    statusFilter,
    dateRange,
    reloadKey,
  ]);

  /* Coming back to this tab re-asks for the list.

     The fetch lives inside the effect above rather than in a function that
     can be called, and its de-dupe is keyed on the parameters — which are
     identical on a return visit, so clearing the key alone would not start
     anything. Bumping a counter that the effect depends on is what re-runs
     it; clearing the key is what stops the de-dupe swallowing the result. */
  useTabRefresh(() => {
    lastRequestRef.current = null;
    setReloadKey((k) => k + 1);
  });

  // Load Dropdowns
  // Dropdown data — load once on mount (StrictMode-safe).
  useEffectOnce(() => {
    dispatch(lastEndTimeApis({}));
    dispatch(projectMenuApis({}));
  });

  useEffect(() => {
    if (formData.project)
      dispatch(clientMenuApis({ projectCode: formData.project }));
  }, [dispatch, formData.project]);

  useEffect(() => {
    if (formData.client) {
      dispatch(mainMenuApis({ clientCode: formData.client }));
      dispatch(raisedbyDetailsApis({ clientCode: formData.client }));
    }
  }, [dispatch, formData.client]);

  useEffect(() => setCurrentPage(1), [activeTab]);

  // ==============================
  // 5. HANDLERS
  // ==============================
  const openAddModal = () => {
    const defaultStart = getDefaultStartTime();
    const currentEnd = getCurrentDateTimeLocal();
    setMinDateTime(defaultStart);
    setFormData({
      ...initialFormState,
      startTime: defaultStart,
      endTime: currentEnd,
    });
    setIsEditMode(false);
    setErrors({});
    setShowModal(true);
  };

  const handleCloseAndReset = () => {
    setShowModal(false);
    setFormData(initialFormState);
    setErrors({});
    setIsEditMode(false);
    setShowRaisedByMenu(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "raisedBy") {
        const foundUser = Array.isArray(raisedByList)
          ? raisedByList.find(
            (u) => u.Client_UserName.toLowerCase() === value.toLowerCase(),
          )
          : null;
        if (foundUser) updated.contactNo = foundUser.MobileNo;
      }

      if (name === "project")
        return {
          ...initialFormState,
          startTime: getDefaultStartTime(),
          endTime: getCurrentDateTimeLocal(), // <--- Add this line here too
          project: value,
          raisedBy: prev.raisedBy,
          contactNo: prev.contactNo,
        };

      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRaisedBySelect = (user) => {
    setFormData((prev) => ({
      ...prev,
      raisedBy: user.Client_UserName,
      contactNo: user.MobileNo,
    }));
    setShowRaisedByMenu(false);
    if (errors.raisedBy) setErrors((prev) => ({ ...prev, raisedBy: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;
    const req = (val, field) => {
      if (!val || String(val).trim().length === 0) {
        newErrors[field] =
          `${field.replace(/([A-Z])/g, " $1").trim()} is required`;
        isValid = false;
      }
    };
    req(formData.project, "project");
    req(formData.client, "client");
    req(formData.raisedBy, "raisedBy");
    req(formData.taskName, "taskName");
    req(formData.contactNo, "contactNo");
    req(formData.startTime, "startTime");
    req(formData.endTime, "endTime");

    /* The server takes exactly ten digits (supportTeamTaskCreateValidation) and
       the form only checked that something was typed — so a number with spaces
       in it, a +91, or nine digits filled the whole dialog in and was refused
       on submit, by a toast naming a field nothing had flagged. Same rule as
       the Tech Team Tasks screen. */
    if (formData.contactNo && !/^\d{10}$/.test(String(formData.contactNo).trim())) {
      newErrors.contactNo = "Contact No must be exactly 10 digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
    const apiPayload = {
      queryNo: isEditMode ? formData.queryNo || null : null,
      projectCode: formData.project,
      clientCode: formData.client,
      raisedBy: formData.raisedBy,
      contactNo: formData.contactNo,
      taskName: formData.taskName,
      description: formData.description,
      isSupportTeam: true,
      status: "Complete",
      queryType: formData.queryType || "",
      ...(formData.taskStatus && { status: formData.taskStatus }),
      timeTaken: getTimeDiffInSeconds(formData.startTime, formData.endTime),
      startTime: formatDateForPayload(formData.startTime),
      endTime: formatDateForPayload(formData.endTime),
    };

    const result = await dispatch(supportTeamTaskCreateApis(apiPayload));
    console.log(result, "result 433433");

    dispatch(
      createStatusTrackerApis({
        queryCode: result?.payload?.data?.[0]?.QueryCode,
        status: "In Progress",
        holdReason: null,
        isSupportTeamTask: 1,
        changeTime: formatDateForPayload(formData.startTime),
      }),
    );

    dispatch(
      createStatusTrackerApis({
        queryCode: result?.payload?.data?.[0]?.QueryCode,
        status: "Complete",
        holdReason: null,
        isSupportTeamTask: 1,
        changeTime: formatDateForPayload(formData.endTime),
      }),
    );
    if (supportTeamTaskCreateApis.fulfilled.match(result)) {
      toast.success(
        isEditMode
          ? "Task Updated Successfully!"
          : "New Task Created Successfully!",
      );
      handleCloseAndReset();

      dispatch(
        supportTeamTaskListApis({
          isManuallyRaised: activeTab === "manual" ? 1 : 0,
          currentPage,
          pageSize,
          statusFilter:
            statusFilter === "All" ? "" : statusFilter.toLowerCase(),
          startDate: dateRange.startDate
            ? moment(dateRange.startDate).format("YYYY-MM-DD")
            : "",
          endDate: dateRange.endDate
            ? moment(dateRange.endDate).format("YYYY-MM-DD")
            : "",
        }),
      );
    }
    dispatch(lastEndTimeApis({}));
    setIsLoading(false);
  };

  const handleExport = (data) => alert(`Exporting ${data.length} tasks...`);

  // --- COLUMNS CONFIG ---
  const columns = useMemo(() => {
    const baseColumns = [
      {
        label: "Query No",
        key: "QueryNo",
        width: "100px",
        render: (r) => (
          <span className="fw-bold text-secondary">#{r.QueryNo}</span>
        ),
      },
      {
        label: "Date",
        key: "FormattedDate",
        width: "110px",
        type: "date",
        render: (r) => r.FormattedDate,
      },

      {
        label: "Start Time",
        key: "StartTime",
        width: "150px",
        type: "datetime",
        render: (r) =>
          formatDateTimeDisplay(r.StartTime),
      },
      {
        label: "End Time",
        key: "EndTime",
        width: "150px",
        type: "datetime",
        render: (r) =>
          formatDateTimeDisplay(r.EndTime),
      },
      {
        label: "Time Taken",
        key: "TimeTaken",
        width: "100px",
        render: (r) => {
          const durationObj = calculateDuration(r.StartTime, r.EndTime);
          return durationObj ? durationObj.display : "-";
        },
        exportFormatter: (r) => {
          const durationObj = calculateDuration(r.StartTime, r.EndTime);
          return durationObj ? durationObj.display : "-";
        },
      },
      {
        label: "Client",
        key: "DisplayClient",
        width: "130px",
        cellClass: "text-cell-wrap",
        render: (r) => <span className="fw-medium">{r.DisplayClient}</span>,
      },
    ];

    const statusCol = {
      label: "Status",
      key: "Status",
      width: "120px",
      render: (r) => {
        let badgeClass = "bg-secondary";
        const s = r.Status ? r.Status.toLowerCase() : "";
        if (s.includes("complete")) badgeClass = "bg-success";
        else if (s.includes("progress")) badgeClass = "bg-primary";
        else if (s.includes("hold") || s.includes("pending"))
          badgeClass = "bg-warning text-dark";
        return (
          <span
            className={`badge ${badgeClass}`}
            style={{ padding: "6px 10px", borderRadius: "12px" }}
          >
            {r.Status || "Open"}
          </span>
        );
      },
    };

    if (activeTab === "manual") {
      return [
        {
          label: "Task Name",
          key: "TaskName",
          width: "250px",
          cellClass: "text-cell-wrap",
          render: (r) => (
            <span className="text-primary fw-medium">
              {r.TaskName || r.NatureofQuery}
            </span>
          ),
        },
        ...baseColumns,
        { label: "Raised By", key: "Client_UserName", width: "120px" },
        statusCol,
      ];
    } else {
      return [
        ...baseColumns,
        {
          label: "Nature of Query",
          key: "NatureofQuery",
          width: "350px",
          cellClass: "text-cell-wrap",
          render: (r) => (
            <span className="text-primary">{r.NatureofQuery}</span>
          ),
        },
        { label: "Raised By", key: "Client_UserName", width: "120px" },
        statusCol,
      ];
    }
  }, [activeTab]);

  return (
    <div className="report-container">
      {/* 1. HEADER */}
      {/* <div className="page-report-header">
        <h4 className="report-title">Support Team Task Management</h4>

        <div className="header-right-controls">
          <div
            className="w-100"
            style={{ minHeight: "40px", marginRight: "10px" }}
          >
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateChange}
            />
          </div>

          <div className="header-btn-group">
            <button
              className="btn btn-primary header-btn"
              onClick={openAddModal}
            >
              + New
            </button>
          </div>
        </div>
      </div> */}
      <div className="support-team-task-header">
        <div className="support-team-task-page">
          <PageHeader
            icon={<i className="bi bi bi-people-fill"></i>}
            title="Support Team Task"
            subtitle="Manage support team tasks and monitor progress"
            actions={
              <>
                <div className="support-task-date-filter">
                  <DateRangeFilter
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onChange={handleDateChange}
                  />
                </div>

                <button
                  className="btn btn-primary header-btn support-task-new-btn"
                  onClick={openAddModal}
                >
                  + New
                </button>
              </>
            }
          />
        </div>

        {/* 3. TABLE WITH LOADER */}
        {listStatus === "loading" || isLoading ? (
          <Loader isLoading={true} />
        ) : (
          <ReportTable
            data={processedData}
            columns={columns}
            onExport={handleExport}
            stickyColumns={["TaskName"]}
          />
        )}

        {/* 4. MODALS */}
        <Modal
          show={showModal}
          onClose={handleCloseAndReset}
          title={isEditMode ? "Edit Task" : "Add New Task"}
          maxWidth="850px"
        >
          <form onSubmit={handleSubmit} className="modal-form-body p-4">
            <div className="form-section-title">Project Context</div>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <SelectInput
                  label="Project *"
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  options={projectOptions}
                  error={errors.project}
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Client *"
                  name="client"
                  value={formData.client}
                  onChange={handleChange}
                  options={clientOptions}
                  error={errors.client}
                  disabled={!formData.project}
                />
              </div>
            </div>

            <div className="form-section-title">Task Details</div>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <TextInput
                  label="Task Name *"
                  name="taskName"
                  value={formData.taskName}
                  onChange={handleChange}
                  error={errors.taskName}
                  placeholder="Brief summary of the task..."
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Query Type"
                  name="queryType"
                  value={formData.queryType}
                  onChange={handleChange}
                  options={queryTypeOptions}
                />
              </div>

              {/* --- TIME FIELDS --- */}
              <div className="row g-3">
                <div className="col-md-6">
                  <TextInput
                    label="Start Time *"
                    name="startTime"
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={handleChange}
                    error={errors.startTime}
                  />
                </div>
                <div className="col-md-6">
                  <TextInput
                    label="End Time *"
                    name="endTime"
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={handleChange}
                    error={errors.endTime}
                    min={formData.startTime || minDateTime}
                  />
                </div>

                <div className="">
                  <div className="col-12 text-start">
                    {formData.startTime && formData.endTime ? (
                      <span className="text-success fw-bold" style={{ fontSize: "14px" }}>
                        Total Time Taken: {calculateDuration(formData.startTime, formData.endTime)?.display || "0 mins"}
                      </span>
                    ) : (
                      <span className="text-muted" style={{ fontSize: "13px" }}>
                        Select both times to see total duration
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold small text-muted text-uppercase mb-2">
                Description
              </label>
              <textarea
                className="form-control modern-textarea"
                name="description"
                rows="4"
                value={formData.description}
                onChange={handleChange}
                placeholder="Detailed description..."
              ></textarea>
            </div>

            <div className="form-section-title">Reporter Information</div>
            <div className="row g-3 mb-4">
              <div className="col-md-6 position-relative">
                <div style={{ position: "relative" }}>
                  <TextInput
                    label="Raised By *"
                    name="raisedBy"
                    value={formData.raisedBy}
                    onChange={handleChange}
                    error={errors.raisedBy}
                    autoComplete="off"
                    onFocus={() => setShowRaisedByMenu(true)}
                    onBlur={() =>
                      setTimeout(() => setShowRaisedByMenu(false), 200)
                    }
                    placeholder="Search user..."
                  />
                  {showRaisedByMenu && (
                    <div
                      className="custom-dropdown-menu"
                      style={{ marginTop: "-10px" }}
                    >
                      {filteredRaisedByList.length > 0 ? (
                        filteredRaisedByList.map((user, idx) => (
                          <div
                            key={idx}
                            className="custom-dropdown-item"
                            onClick={() => handleRaisedBySelect(user)}
                          >
                            <div className="fw-medium">
                              {user.Client_UserName}
                            </div>
                            <small
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {user.MobileNo || "No Contact"}
                            </small>
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-empty">
                          No users found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <TextInput
                  label="Contact No *"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleChange}
                  error={errors.contactNo}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
              <button
                type="button"
                className="btn btn-light px-4 py-2 fw-medium text-secondary border"
                onClick={handleCloseAndReset}
                style={{ borderRadius: "8px" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4 py-2 fw-medium shadow-sm"
                disabled={isLoading}
                style={{ borderRadius: "8px", minWidth: "140px" }}
              >
                {createStatus === "loading" ? (
                  <span>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </span>
                ) : isEditMode ? (
                  "Update Task"
                ) : (
                  "Create Task"
                )}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default SupportTeamTask;
